import {makeAutoObservable, runInAction} from 'mobx';
import {FEATURES} from '../models/Plan';
import SettingStore from './setting-store';
import PortalService, {IUserFeature} from '../services/api/user-service';
import {rootContainer, TYPE} from '../services/container/root';
import {IAddon} from "../models/Addon";
import PlatformServiceStore from './platform-service-store';
import currentAccountStore from '../pages/accounts/stores/current-account-store';
import UserStore from '../stores/user-store';

class CurrentPortalStore {
    private _currentPortal: UserStore = new UserStore();
    private _setting?: SettingStore;
    private _platform?: PlatformServiceStore;
    private _current_user_addons?: IAddon[];
    private _isLoading: boolean = false;
    private _isLoadingFeatures: boolean = false;
    private _userFeaturesMap = new Map<FEATURES, IUserFeature>();
    private _wasLoaded: boolean = false; /** an indication to prevent loading this store over and over */

    private get portalService(): PortalService {
        return rootContainer.get<PortalService>(TYPE.UserService);
    }

    constructor() {
        makeAutoObservable(this);
    }

    public get isLoading(): boolean {
        return this._isLoading;
    }

    public get currentPortal(): UserStore {
        return this._currentPortal;
    }

    public get currencyCode(): string {
        return this.setting?.currencyCode ?? 'USD';
    }

    public get allowUnfulfilled(): boolean | undefined {
        return this._currentPortal?.setting?.allow_unfulfilled;
    }

    public get setting(): SettingStore | undefined {
        return this._setting;
    }

    public get platform(): PlatformServiceStore | undefined {
        return this._platform;
    }

    public get currentUserAddons(): IAddon[] | undefined {
        return this._current_user_addons;
    }

    public get hasMissingTranslations(): boolean | undefined {
        return this._currentPortal.hasMissingTranslations;
    }

    async load() {
        if (this._wasLoaded || this._isLoading) {
            return;
        }

        try {
            this._isLoading = true;
            await Promise.all([
                this._currentPortal.load(),
                this.loadFeatures([
                    FEATURES.GIFT_RETURNS,
                    FEATURES.AUTO_EXCHANGE,
                    FEATURES.PRODUCT_EXCHANGE,
                    FEATURES.VALIDATE_ITEMS,
                    FEATURES.BXGY,
                    FEATURES.RESERVE_ITEMS,
                    FEATURES.CANCEL_ORDER,
                    FEATURES.RETURN_SCORE_RESOLUTION,
                    FEATURES.ORDER_TRACKING,
                    FEATURES.INSTANT_EXCHANGE,
                    FEATURES.BETA_VALIDATION_RESTOCK_TRIGGER_AUTOMATION,
                    FEATURES.ORDER_TRACKING,
                    FEATURES.SWITCH_TO_EXCHANGE,
                    FEATURES.PORTAL_CHECKOUT,
                    FEATURES.AUTO_REFUND,
                    FEATURES.MARK_AS_DONE,
                    FEATURES.COMPARE_AT_PRICE_DISCOUNT,
                    FEATURES.MANAGER_APPROVAL_SETTING_FEATURE,
                    FEATURES.CANCEL_INACTIVE_RMAS_FEATURE,
                    FEATURES.ANY_TO_ANY_EXCHANGES_FEATURE,
                    FEATURES.BETA_SHOW_RMA_SCORE_ON_RETURNS_PAGE,
                    FEATURES.BETA_SUPPORT_MULTIPLE_SHIPMENTS_FEATURE,
                    FEATURES.COVERAGE,
                    FEATURES.BETA_CANCEL_SHIPMENT_LABEL,
                    FEATURES.BETA_TRACKING_DB_REPORT,
                    FEATURES.BETA_TRACKING_REPORT,
                    FEATURES.BETA_PRE_AUTHORIZATION,
                    FEATURES.BETA_ROUNDED_LABEL_FEE_COST,
                    FEATURES.BETA_RMS_API_MANAGE_RETURNS,
                    FEATURES.BETA_RUNTIME_ERROR_CLASSIFICATION_DISPLAY,
                    FEATURES.BETA_EXCHANGE_OF_EXCHANGED_ITEMS,
                ]),
                currentAccountStore.load()
            ]);
            runInAction(() => {
                this._isLoading = false;
                this._wasLoaded = true;
                this._platform = new PlatformServiceStore(this.currentPortal.platformService);

                if (this.currentPortal.setting) {
                    this._setting = new SettingStore(this.currentPortal.setting);
                }
            });
        }
        catch (e) {
            console.error(e);
            runInAction(() => {
                this._isLoading = false;
            });
        }
    }

    public requireManagerApproval(): boolean {
        return !!this.setting?.requireManagementApproval;
    }

    async loadFeatures(featureIdList: FEATURES[]): Promise<void> {
        try {
            this._isLoadingFeatures = true;
            const loadedFeatures = await this.portalService.getUserFeatures(featureIdList);
            runInAction(() => {
                this._isLoadingFeatures = false;
                loadedFeatures.forEach(feat => {
                    this._userFeaturesMap.set(feat.feature_id, feat);
                });
            });
        }
        catch (err) {
            runInAction(() => {
                this._isLoadingFeatures = false;
            });
        }
    }

    /** DEPRECATE this method - we used to throw from isFeatureAllowed if the feature was not loaded, now it will simply return false */
    public isFeatureLoaded(featureIdList: FEATURES[]): boolean {
        const notLoadedFeature = featureIdList.find(featureId => {
            let userFeature = this._userFeaturesMap.get(featureId);
            return !userFeature;
        });
        if (notLoadedFeature) {
            return false;
        }

        return true;
    }

    public isFeatureAllowed(featureId: FEATURES): boolean {
        let userFeature = this._userFeaturesMap.get(featureId);
        return userFeature?.is_allowed ?? false;
    }

    public getFeatureQuota(featureId: FEATURES): number {
        let userFeature = this._userFeaturesMap.get(featureId);
        return userFeature?.quota ?? -1;
    }
}

export default new CurrentPortalStore();
